/**
 * シナリオ生成AIモデル選択のポータルドロップダウン。
 *
 * シナリオ編集ページの「動画を生成」ドロップダウン (VideoModelDropdownPortal) と
 * 同じ共通部品 (ModelDropdownMenu) で描画し、見た目を揃える。
 * - 最上部フィルタ: すべて / 高品質 / 標準 / スピード (既定はすべて。開くたびリセット)
 * - モデルファミリー ('Claude' / 'GPT' / 'Gemini') ごとにアイコン付き見出しでグルーピング
 * - 各項目はモデル名 + pt バッジ。ランダム枠はフィルタ「すべて」のときのみ先頭に表示
 */
import { useEffect, useState } from 'react';
import {
  ModelDropdownItem,
  ModelDropdownMenuShell,
  ModelDropdownSectionHeader,
  ModelDropdownSeparator,
} from '../common/ModelDropdownMenu';
import { SCENARIO_MODEL_RANDOM } from '../state/useGenerationState';
import type { ScenarioModelDef } from './hooks/useScenarioModelOptions';
import type { UsePortalDropdownReturn } from './hooks/usePortalDropdown';

const TIER_FILTERS = [
  { value: 'all', label: 'すべて' },
  { value: '高品質', label: '高品質' },
  { value: '標準', label: '標準' },
  { value: 'スピード', label: 'スピード' },
] as const;

interface ScenarioModelDropdownPortalProps {
  dropdown: UsePortalDropdownReturn;
  scenarioModelDefs: ScenarioModelDef[];
  /** ランダム枠の 1 本あたり消費レンジ表記 ("2~30" 等)。有効モデルなしなら undefined */
  randomRateBadge: string | undefined;
  scenarioModel: string;
  setScenarioModel: (model: string) => void;
}

export function ScenarioModelDropdownPortal({
  dropdown,
  scenarioModelDefs,
  randomRateBadge,
  scenarioModel,
  setScenarioModel,
}: ScenarioModelDropdownPortalProps) {
  const [tierFilter, setTierFilter] = useState<string>('all');

  // 開くたびに「すべて」へリセットする (前回のフィルタが残ると選択中モデルが見えないことがある)
  useEffect(() => {
    if (dropdown.openId) setTierFilter('all');
  }, [dropdown.openId]);

  if (!dropdown.openId || !dropdown.pos) return null;

  // credit_rate_config で無効化されたモデルは除外 (SingleSelectDropdown 時代と同じ挙動)
  const activeDefs = scenarioModelDefs.filter((d) => d.rate !== null);
  const filteredDefs = activeDefs.filter((d) => tierFilter === 'all' || d.tier === tierFilter);

  const items: React.ReactNode[] = [];

  // ランダム枠: 有効モデルが 1 つ以上あり、フィルタが「すべて」のときのみ先頭へ
  // (ランダムは全ティアからの抽選なので、ティア絞り込み中は表示しない)
  if (activeDefs.length > 0 && tierFilter === 'all') {
    items.push(
      <ModelDropdownItem
        key="random"
        label={<><span style={{ fontWeight: 600 }}>ランダム</span>（各モデルから1案）</>}
        pts={randomRateBadge !== undefined ? `${randomRateBadge}pt` : undefined}
        active={scenarioModel === SCENARIO_MODEL_RANDOM}
        onSelect={() => {
          setScenarioModel(SCENARIO_MODEL_RANDOM);
          dropdown.close();
        }}
      />,
    );
  }

  // カタログ順を保ったままファミリー別にグルーピング
  const familyOrder: string[] = [];
  const grouped = new Map<string, ScenarioModelDef[]>();
  for (const def of filteredDefs) {
    if (!grouped.has(def.family)) {
      grouped.set(def.family, []);
      familyOrder.push(def.family);
    }
    grouped.get(def.family)!.push(def);
  }

  familyOrder.forEach((family) => {
    const defs = grouped.get(family)!;
    if (items.length > 0) {
      items.push(<ModelDropdownSeparator key={`sep-${family}`} />);
    }
    items.push(
      <ModelDropdownSectionHeader key={`hdr-${family}`} label={family} iconMask={defs[0].icon} />,
    );
    defs.forEach((def) => {
      items.push(
        <ModelDropdownItem
          key={def.value}
          label={(
            <>
              <span style={{ fontWeight: 600 }}>{def.tier}</span>
              <span style={{ marginLeft: '6px' }}>{def.sublabel}</span>
            </>
          )}
          pts={def.rate !== null ? `${def.rate}pt` : undefined}
          active={scenarioModel === def.value}
          onSelect={() => {
            setScenarioModel(def.value);
            dropdown.close();
          }}
        />,
      );
    });
  });

  return (
    <ModelDropdownMenuShell
      menuRef={dropdown.menuRef}
      pos={dropdown.pos}
      filter={{
        label: '品質',
        options: TIER_FILTERS.map((t) => ({ value: t.value, label: t.label })),
        value: tierFilter,
        onChange: setTierFilter,
      }}
      emptyText="この条件に一致するモデルがありません"
    >
      {items}
    </ModelDropdownMenuShell>
  );
}
